import { useMemo } from "react";
import { type Identifier, useGetIdentity, useGetList } from "ra-core";

import {
  isDone,
  isDueLater,
  isDueThisWeek,
  isDueToday,
  isDueTomorrow,
  isOverdue,
  isRecentlyDone,
} from "./tasksPredicate";

export type TaskSection =
  "overdue" | "today" | "tomorrow" | "this_week" | "later";

export const ALL_SECTIONS: TaskSection[] = [
  "overdue",
  "today",
  "tomorrow",
  "this_week",
  "later",
];

/**
 * My open tasks (or a contact's) grouped by due date. Shared by the task
 * lists and the counters of «Сегодня», so both make the same request.
 */
export const useTasksByDueDate = (filterByContact?: Identifier) => {
  const { identity } = useGetIdentity();
  const { data: tasks, isPending } = useGetList(
    "tasks",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
      filter: {
        ...(filterByContact != null
          ? { contact_id: filterByContact }
          : { sales_id: identity?.id }),
      },
    },
    { enabled: filterByContact != null ? true : !!identity },
  );

  const tasksBySection = useMemo(() => {
    const ongoing =
      tasks?.filter((task) => !isDone(task) || isRecentlyDone(task)) ?? [];
    return {
      overdue: ongoing.filter((task) => isOverdue(task.due_date)),
      today: ongoing.filter((task) => isDueToday(task.due_date)),
      tomorrow: ongoing.filter((task) => isDueTomorrow(task.due_date)),
      this_week: ongoing.filter((task) => isDueThisWeek(task.due_date)),
      later: ongoing.filter((task) => isDueLater(task.due_date)),
    } satisfies Record<TaskSection, typeof ongoing>;
  }, [tasks]);

  return { tasksBySection, isPending };
};
